import { createReducer, current, isDraft } from "@reduxjs/toolkit";
import {
  getPageModules,
  getSiteModules as getSiteModulesSelector,
} from "../../selectors/modules.js";
import { Language, Module, Modules } from "../../types/index.js";
import {
  creatableGlobalModules,
  getModulesByParentId,
  isItemUntranslated,
  ModuleNotFoundError,
} from "../../utils/utils.js";
import { deletePageTranslation, postPageTranslation } from "../pages.js";
import { initialState as actionModulesInitialState } from "./actionModules.js";
import { deleteModuleTranslationStart } from "./actions.js";
import { initialState as byIdInitialState } from "./byId.js";
import { initialState as byPageIdInitialState } from "./byPageId.js";
import { initialState as byParentIdInitialState } from "./byParentId.js";
import { initialState as bySiteModuleTypeInitialState } from "./bySiteModuleType.js";
import { combined } from "./index.js";
import { postMoveModule, translateModule } from "./thunks.js";

const initialState: Modules = {
  actionModules: actionModulesInitialState,
  byId: byIdInitialState,
  byPageId: byPageIdInitialState,
  byParentId: byParentIdInitialState,
  bySiteModuleType: bySiteModuleTypeInitialState,
};

const postPageTranslationInternal = (
  state: Modules,
  pageId: string,
  languageId: Language,
  sourceLanguageId: Language,
) => {
  const siteModules = getSiteModulesSelector(state);
  const modules = getPageModules(state, pageId);

  return [...siteModules, ...modules].reduce((newState, module) => {
    const action = createTranslateModulePendingAction(
      module,
      pageId,
      languageId,
      sourceLanguageId,
    );

    return translateSubModules(newState, action);
  }, state);
};

const translateSubModules = (
  state: Modules,
  action: ReturnType<typeof translateModule.pending>,
): Modules => {
  const {
    moduleId,
    languageId,
    sourceLanguageId,
    pageId: actionPageId,
  } = action.meta.arg;
  const currentModule = state.byId[moduleId];
  const pageId =
    currentModule && creatableGlobalModules.includes(currentModule.type)
      ? currentModule.pageId
      : actionPageId;
  const modules = getModulesByParentId(state, moduleId, pageId);
  state = combined(isDraft(state) ? current(state) : state, action);

  return modules.reduce<Modules>(
    (newState, module) =>
      translateSubModules(
        newState,
        createTranslateModulePendingAction(
          module,
          pageId,
          languageId,
          sourceLanguageId,
        ),
      ),
    state,
  );
};

const deletePageTranslationInternal = (
  state: Modules,
  pageId: string,
  languageId: Language,
) => {
  const modules = getPageModules(state, pageId);

  return modules.reduce((newState, module) => {
    const action = deleteModuleTranslationStart({
      pageId,
      languageId,
      moduleId: module.id,
      moduleType: module.type,
      parentId: module.parentId,
      deleteAllTranslations: isItemUntranslated(module),
    });
    return deleteSubModuleTranslation(newState, action);
  }, state);
};

const deleteSubModuleTranslation = (
  state: Modules,
  action: ReturnType<typeof deleteModuleTranslationStart>,
): Modules => {
  const { moduleId, pageId, moduleType, languageId } = action.payload;
  const modules = getModulesByParentId(state, moduleId, pageId, moduleType);
  state = combined(isDraft(state) ? current(state) : state, action);

  return modules.reduce<Modules>((newState, module) => {
    return deleteSubModuleTranslation(
      newState,
      deleteModuleTranslationStart({
        pageId,
        languageId,
        moduleId: module.id,
        moduleType: module.type,
        parentId: module.parentId,
        deleteAllTranslations: isItemUntranslated(module),
      }),
    );
  }, state);
};

const createTranslateModulePendingAction = (
  module: Module,
  pageId: string | null,
  languageId: Language,
  sourceLanguageId: Language,
) =>
  translateModule.pending(crypto.randomUUID(), {
    siteId: module.siteId,
    moduleId: module.id,
    languageId,
    sourceLanguageId,
    pageId,
  });

const reducer = createReducer(initialState, (builder) => {
  builder.addCase(translateModule.pending, (state, action) =>
    translateSubModules(state, action),
  );

  builder.addCase(deleteModuleTranslationStart, (state, action) =>
    deleteSubModuleTranslation(state, action),
  );

  builder.addCase(postPageTranslation.pending, (state, action) => {
    const { pageId, languageId } = action.meta.arg;
    const { sourceLanguageId } = action.meta;
    return postPageTranslationInternal(
      state,
      pageId,
      languageId,
      sourceLanguageId,
    );
  });

  builder.addCase(deletePageTranslation.pending, (state, action) => {
    const { pageId, languageId } = action.meta.arg;
    return deletePageTranslationInternal(state, pageId, languageId);
  });

  builder.addCase(postMoveModule.rejected, (state, action) => {
    if (action.meta.condition) {
      const { moduleId } = action.meta.arg;
      const module = state.byId[moduleId];
      if (!module) throw new ModuleNotFoundError(moduleId);
    }
  });
});

export default reducer;
